'use client';

import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';

const LoginForm = () => {
  const router = useRouter();
  const { signIn, isAdmin, user, loading: authLoading } = useAuth();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // NEW: track that admin login was attempted
  const [loginAttempted, setLoginAttempted] = useState(false);

  /* -------------------- helpers -------------------- */

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = 'Admin email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid admin email';
    }

    if (!formData.password) {
      newErrors.password = 'Admin password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  /* -------------------- submit -------------------- */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsLoading(true);
    setErrors({});
    setLoginAttempted(false);

    try {
      const { error } = await signIn(formData.email, formData.password);

      if (error) {
        setErrors({
          submit: error.message || 'Admin login failed. Check credentials.'
        });
        setIsLoading(false);
        return;
      }

      // mark that admin login flow started
      setLoginAttempted(true);

      // remember me
      if (formData.rememberMe) {
        localStorage.setItem('adminRememberedEmail', formData.email);
      } else {
        localStorage.removeItem('adminRememberedEmail');
      }

    } catch (err) {
      setErrors({
        submit: 'An unexpected error occurred. Please try again.'
      });
      setIsLoading(false);
    }
  };

  /* -------------------- IMPORTANT FIX -------------------- */
  /* Wait for AuthContext to finish loading profile */

  useEffect(() => {
    if (!loginAttempted) return;
    if (authLoading) return;
    if (!user) return;

    if (!isAdmin) {
      setErrors({
        submit: 'You are not authorized as admin'
      });
      setIsLoading(false);
      setLoginAttempted(false);
      return;
    }

    // ✅ ADMIN VERIFIED
    router.push('/admin-dashboard');
  }, [loginAttempted, authLoading, user, isAdmin, router]);

  /* -------------------- UI (UNCHANGED) -------------------- */

  return (
    <form onSubmit={handleSubmit} className="w-full space-y-6" noValidate>

      {/* Email */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground caption">
          Admin Email <span className="text-error">*</span>
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Icon name="EnvelopeIcon" size={20} className="text-muted-foreground" />
          </div>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="admin@athena.com"
            disabled={isLoading || authLoading}
            className={`w-full h-12 pl-12 pr-4 bg-card border rounded-md text-sm focus:outline-none ${
              errors.email ? 'border-error' : 'border-border'
            }`}
          />
        </div>
        {errors.email && <p className="text-sm text-error">{errors.email}</p>}
      </div>

      {/* Password */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-foreground caption">
          Admin Password <span className="text-error">*</span>
        </label>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Icon name="LockClosedIcon" size={20} className="text-muted-foreground" />
          </div>
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            value={formData.password}
            onChange={handleChange}
            placeholder="admin@123"
            disabled={isLoading || authLoading}
            className={`w-full h-12 pl-12 pr-12 bg-card border rounded-md text-sm focus:outline-none ${
              errors.password ? 'border-error' : 'border-border'
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute inset-y-0 right-0 pr-4"
          >
            <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={20} />
          </button>
        </div>
        {errors.password && <p className="text-sm text-error">{errors.password}</p>}
      </div>

      {/* Remember Me */}
      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          name="rememberMe"
          checked={formData.rememberMe}
          onChange={handleChange}
        />
        <span className="text-sm">Remember me</span>
      </label>

      {/* Submit Error */}
      {errors.submit && (
        <div className="p-4 bg-error/10 border border-error rounded-md text-error">
          {errors.submit}
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={isLoading || authLoading}
        className="w-full h-12 bg-primary text-primary-foreground rounded-md"
      >
        {isLoading || authLoading ? 'Admin Sign In...' : 'Admin Sign In'}
      </button>

      {/* Customer Login */}
      <div className="text-center pt-4 border-t border-border">
        <Link href="/login" className="text-primary">
          Go to Customer Login →
        </Link>
      </div>

    </form>
  );
};

export default LoginForm;